// Mobile/tablet (< xl) wrapper around MinimapTimeline. Opens as a right-side
// sheet via the header button and renders the conversation outline (the same
// timeline used by the persistent dock at >= xl). Opens pinned to the live end
// of the conversation; tapping a turn jumps to it AND dismisses the sheet. A
// footer button jumps to the end of the conversation.

import { ArrowDown } from "lucide-react";
import type { JSX } from "react";

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import type { MinimapItem } from "./Minimap";
import { MinimapTimeline } from "./MinimapTimeline";

interface MinimapBottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: MinimapItem[];
  /** Id of the message the user was reading when the sheet opened. The turn
   * containing it renders as the active step. `null`/absent = no active
   * highlight (e.g. the chat hasn't been scrolled). */
  activeItemId?: string | null;
  onJumpTo: (itemId: string) => void;
  /** Scroll the chat to the very bottom (footer shortcut). */
  onJumpToEnd: () => void;
}

export function MinimapBottomSheet({
  open,
  onOpenChange,
  items,
  activeItemId,
  onJumpTo,
  onJumpToEnd,
}: MinimapBottomSheetProps): JSX.Element {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} ariaLabel="Minimapa de mensajes">
      <SheetContent side="right" onClose={() => onOpenChange(false)} className="w-full max-w-md">
        <SheetHeader>
          <SheetTitle>Minimapa</SheetTitle>
        </SheetHeader>
        <SheetBody>
          <MinimapTimeline
            items={items}
            activeItemId={activeItemId ?? null}
            onJumpTo={(id) => {
              onJumpTo(id);
              onOpenChange(false);
            }}
            pinToBottomOnMount
          />
        </SheetBody>
        {items.length > 0 ? (
          <SheetFooter className="justify-center">
            <button
              type="button"
              onClick={() => {
                onJumpToEnd();
                onOpenChange(false);
              }}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-muted px-3 py-2 text-body-3 font-medium text-foreground transition-colors hover:bg-muted/70"
            >
              Ir al final de la conversación
              <ArrowDown size={14} />
            </button>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
